import { User } from '../lib/api/types/user';

export interface FreeAccessStatus {
  isFreeAccount: boolean;
  isParcoursUsed: boolean;
  parcoursType: string | null;
  expiryDate: Date | null;
  daysRemaining: number | null;
}

/**
 * Calcule le nombre de jours restants avant une date d'expiration.
 */
function computeDaysRemaining(expiryDate: Date | null): number | null {
  if (!expiryDate) return null;
  const now = new Date();
  const diff = new Date(expiryDate).getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/**
 * Hook permettant de connaître le statut d'accès gratuit de l'utilisateur courant.
 * Prend directement l'objet `user` (déjà chargé via le contexte auth) pour éviter
 * un appel réseau supplémentaire.
 *
 * @example
 * const status = useFreeAccessCheck(currentUser);
 * if (status.isFreeAccount) { ... }
 */
const PARCOURS_TYPE_LABELS: Record<string, string> = {
  ARBRE: 'Arbre',
  ALIGNMENT: 'Alignement',
  EPAYSAGE: 'Espace boisé',
};

export function formatFreeParcoursTypeLabel(type: string | null | undefined): string {
  if (!type) return 'parcours';
  return PARCOURS_TYPE_LABELS[type] ?? type;
}

export function useFreeAccessCheck(user: User | null | undefined): FreeAccessStatus {
  const groupe = user?.groupe;

  const isFreeAccount = groupe?.plan_type === 'FREE';
  const isParcoursUsed = isFreeAccount ? (groupe?.free_parcours_used ?? false) : false;
  const parcoursType = isFreeAccount ? (groupe?.free_parcours_type ?? null) : null;
  const expiryDate = isFreeAccount ? (groupe?.date_echeance ? new Date(groupe.date_echeance) : null) : null;
  const daysRemaining = computeDaysRemaining(expiryDate);

  return {
    isFreeAccount,
    isParcoursUsed,
    parcoursType,
    expiryDate,
    daysRemaining,
  };
}
